import { useState } from 'react';
import { IconPencil } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Segmented } from '@hana/ui';
import { KPI_CARDS, KPI_POS, MET, SRC, f1, fi, fmtRaw, gradeOf, isReal, type PersonCard } from './kpiPeople';
import './i18n';
import s from './kpiPerson.module.css';

// Mint-KPI · tab Theo người (code board: tab === 'person'). Bảng Nhân sự + thẻ cá nhân. Không tiền.

const GRADE_FILL: Record<string, string> = { A: 'var(--ok)', B: 'var(--d-blue)', C: 'var(--d-amber)', D: 'var(--err)' };
const GRADE_CHIP: Record<string, [string, string]> = { A: ['var(--ok-bg)', 'var(--ok)'], B: ['var(--pri-bg)', 'var(--link)'], C: ['var(--warn-bg)', 'var(--warn)'], D: ['var(--err-bg)', 'var(--err)'] };
const heat = (sc: number) => (sc >= 90 ? 'var(--ok-bg)' : sc >= 75 ? 'var(--pri-bg)' : sc >= 60 ? 'var(--warn-bg)' : 'var(--err-bg)');

export function usePersonSegs() {
  const t = useT();
  const [pf, setPf] = useState('');
  const segs = (
    <div className={s.segs}>
      <Segmented kind="toggle" label={t('hr.kp.dept')} value={pf} onChange={setPf} items={[{ key: '', label: t('hr.kp.all') }, ...KPI_POS.map((p) => ({ key: p.key, label: p.label }))]} />
    </div>
  );
  return { pf, segs };
}

function Chip({ g }: { g: string }) {
  const c = GRADE_CHIP[g] ?? ['var(--skel)', 'var(--lb)'];
  return <span className={s.chipG} style={{ background: c[0], color: c[1] }}>{g}</span>;
}

export function Card({ p, onNote, mine = false }: { p: PersonCard; onNote: (m: string) => void; mine?: boolean }) {
  const t = useT();
  const gc = GRADE_CHIP[p.grade]?.[1] ?? 'var(--tx)';
  const tiles: [string, string, string, string?][] = [
    [t('hr.kp.base'), p.base == null ? '—' : f1(p.base), t('hr.kp.baseSub')],
    [t('hr.kp.pen'), fi(p.pen), t('hr.kp.penSub'), p.pen ? 'var(--err)' : undefined],
    [t('hr.kp.rew'), fi(p.rew), t('hr.kp.rewSub'), p.rew ? 'var(--ok)' : undefined],
    [t('hr.kp.adj'), '0', t('hr.kp.adjSub')],
    [t('hr.kp.fin'), p.fin == null ? '—' : f1(p.fin), t('hr.kp.grade', { g: p.grade }), gc],
  ];
  return (
    <>
      <section className={s.panel} aria-label={mine ? t('hr.kp.mineLbl', { u: p.u }) : p.u}>
        <div className={s.hd}>
          <h2>{mine ? t('hr.kp.mineHd', { u: p.u }) : p.u}</h2><span>[{p.seat}] · {p.deptK} · {t('hr.kp.confirm', { m: p.mgr })}</span>
          {mine ? null : <button type="button" onClick={() => onNote(t('hr.noWrite'))}><IconPencil size={16} stroke={1.8} aria-hidden />{t('hr.kp.mgrAdj')}</button>}
        </div>
        <div className={s.tiles5}>
          {tiles.map(([k, v, sub, c]) => <div key={k} className={s.tile}><span>{k}</span><b style={c ? { color: c } : undefined}>{v}</b><span>{sub}</span></div>)}
        </div>
      </section>
      {p.raws.length ? (
        <section className={s.panel} aria-label={t('hr.kp.byMetric')}>
          <div className={s.hd}><h2>{t('hr.kp.byMetric')}</h2><span>{t('hr.kp.byMetricSub')}</span></div>
          <div className={`${s.bars} ${mine && p.raws.length > 3 ? s.bars2 : ''}`} role="img" aria-label={`${t('hr.kp.byMetric')}: ${p.raws.map((x) => `${x.m} ${fi(x.sc)}`).join(', ')}`}>
            {p.raws.map((x, i) => (
              <div key={x.m} className={`${s.brow} ${(mine && p.raws.length > 3 ? i >= 2 : i) ? s.top : ''}`}>
                <div className={s.bl}><b>{x.m} · {MET[x.m]?.name}</b></div>
                <div className={s.track}><div className={s.fill} style={{ width: `${Math.max(2, x.sc).toFixed(1)}%`, background: GRADE_FILL[gradeOf(x.sc)] }} /></div>
                <b className={s.bv}>{fi(x.sc)}</b>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      <section className={s.panel} aria-label={t('hr.kp.metrics')}>
        <div className={s.hd}><h2>{t('hr.kp.metrics')}</h2><span>{p.noSet ? t('hr.kp.noSet') : t('hr.kp.weights')}</span></div>
        <table className={s.tbl} aria-label={t('hr.kp.metrics')}>
          <colgroup>{[12, 32, 13, 10, 9, 11, 13].map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}</colgroup>
          <thead><tr>
            <th scope="col">{t('hr.kp.code')}</th><th scope="col">{t('hr.kp.metrics')}</th><th scope="col" className={s.r}>{t('hr.kp.actual')}</th><th scope="col">{t('hr.kp.dir')}</th><th scope="col" className={s.r}>{t('hr.kp.score')}</th><th scope="col">{t('hr.kp.data')}</th><th scope="col">{t('hr.kp.src')}</th>
          </tr></thead>
          <tbody>
            {p.raws.length ? p.raws.map((x) => {
              const d = MET[x.m]!;
              const real = isReal(p.u, x.m);
              return (
                <tr key={x.m}>
                  <td className={s.mc}>{x.m}</td><td>{d.name}</td><td className={s.r}>{fmtRaw(x.raw, d.unit)}</td>
                  <td className={s.lbl}>{d.dir === 'Higher' ? t('hr.kp.higher') : t('hr.kp.lower')}</td>
                  <td className={`${s.r} ${s.sc}`} style={{ background: heat(x.sc) }}>{fi(x.sc)}</td>
                  <td className={real ? s.okc : s.lbl}>{real ? t('hr.kp.real') : t('hr.kp.sample')}</td><td className={s.lbl}>{SRC[x.m]}</td>
                </tr>
              );
            }) : <tr><td colSpan={7} className={s.empty}>{t('hr.kp.noSeatSet', { seat: p.seat })}</td></tr>}
          </tbody>
        </table>
      </section>
      <section className={s.panel} aria-label={t('hr.kp.evHd')}>
        <div className={s.hd}>
          <h2>{t('hr.kp.evHd')}</h2><span>{t('hr.kp.evSub')}</span>
          <button type="button" onClick={() => onNote(t('hr.kp.openLedgerNote'))}>{t('hr.kp.openLedger')}</button>
        </div>
        <div className={s.list}>
          {p.evs.length ? p.evs.map((e, i) => {
            const c = e[1] === 'P' ? GRADE_CHIP.D! : GRADE_CHIP.A!;
            return (
              <div key={e[0]} className={`${s.lrow} ${i ? s.top : ''}`}>
                <span className={s.chipG} style={{ background: c[0], color: c[1] }}>{e[0]}</span>
                <span className={s.lsub}>{e[1] === 'P' ? t('hr.kp.pen1') : t('hr.kp.rew1')}</span><b>{e[2]}</b>
              </div>
            );
          }) : <div className={s.lrow}><span className={s.tx2}>{t('hr.kp.noEv')}</span></div>}
        </div>
      </section>
    </>
  );
}

export function HrKpiPerson({ pf, onNote }: { pf: string; onNote: (m: string) => void }) {
  const t = useT();
  const [sel, setSel] = useState('ThuyNTT');
  const list = KPI_CARDS.filter((x) => !pf || x.dept === pf);
  const p = KPI_CARDS.find((x) => x.u === sel) ?? KPI_CARDS[0]!;
  return (
    <div className={s.grid}>
      <section className={s.panel} style={{ gridColumn: 'span 4', gridRow: `span ${p.mets.length ? 4 : 3}` }} aria-label={t('hr.kp.staff')}>
        <div className={s.hd}><h2>{t('hr.kp.staff')}</h2><span>{t('hr.kp.staffSub', { n: KPI_CARDS.length })}</span></div>
        <table className={s.tbl} aria-label={t('hr.kp.staff')}>
          <colgroup><col style={{ width: '40%' }} /><col style={{ width: '30%' }} /><col style={{ width: '16%' }} /><col style={{ width: '14%' }} /></colgroup>
          <thead><tr><th scope="col">{t('hr.kp.person')}</th><th scope="col">{t('hr.kp.seat')}</th><th scope="col" className={s.r}>{t('hr.kp.score')}</th><th scope="col" className={s.r}>{t('hr.kp.gradeCol')}</th></tr></thead>
          <tbody>
            {list.map((x) => (
              <tr key={x.u} className={s.pick} style={{ background: x.u === p.u ? 'var(--pri-bg)' : undefined }} aria-selected={x.u === p.u}
                tabIndex={0} onClick={() => setSel(x.u)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel(x.u); } }}>
                <td className={s.nm}>{x.u}</td><td className={s.seat}>{x.seat}</td>
                <td className={s.r}>{x.fin == null ? '—' : f1(x.fin)}</td>
                <td className={s.r}>{x.fin == null ? <span className={s.lbl}>—</span> : <Chip g={x.grade} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <div className={s.card}><Card p={p} onNote={onNote} /></div>
    </div>
  );
}
